import { useTranslation } from "@privacy/translations";
import { Box } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import type { SidePanelGroup } from "../../../dynamicGrid/SidePanels";
import {
  REGISTRATION_STATUS_VARIANTS,
  type RegistrationStatus,
  type GridViewRegistration,
} from "../../../api/registrations.types";
import { DynamicGrid } from "../../../dynamicGrid";
import { RegistrationStatusDot } from "../../../shared/RegistrationStatusDot";
import { statusColors } from "../../../shared/RegistrationStatus";
import { getFormatedPercentage, StatsList, StatsListItem } from "./StatsList";
import { SidePanelGrid } from "./SidePanelGrid";

export const StatusSection = ({ filteredRowsData }: { filteredRowsData: GridViewRegistration[] }) => {
  const { t } = useTranslation();
  const theme = useTheme();

  const goToSidePanel = DynamicGrid.useGoToSidePanel();

  const statusMap = getStatusMap(filteredRowsData);

  const chartData = REGISTRATION_STATUS_VARIANTS.map(status => ({
    status,
    value: statusMap.get(status)?.length ?? 0,
    color: statusColors[status].dot(theme),
  })).filter(item => item.value > 0);

  const total = chartData.reduce((sum, item) => sum + item.value, 0);

  return (
    <StatsList
      label={t("RegistrationGrid.SidePanels.Status")}
      labelIcon={
        filteredRowsData.length > 0 && total > 0 ? (
          <Box
            aria-hidden={true}
            sx={{ width: 80, display: "flex", height: 24, gap: "2px", borderRadius: 0.5, overflow: "hidden", mr: 1 }}
          >
            {chartData.map(({ status, value, color }) => {
              const percentage = (value / total) * 100;
              return <Box key={status} sx={{ height: "100%", width: `${percentage}%`, backgroundColor: color }} />;
            })}
          </Box>
        ) : null
      }
    >
      {REGISTRATION_STATUS_VARIANTS.map(status => {
        const statusCount = statusMap.get(status)?.length ?? 0;
        return (
          <StatsListItem
            key={status}
            onClick={() => goToSidePanel(status)}
            icon={<RegistrationStatusDot status={status} />}
            label={t(`RegistrationStatus.${status}`)}
            formattedPercentage={getFormatedPercentage(statusCount, filteredRowsData.length)}
            count={statusCount}
          />
        );
      })}
    </StatsList>
  );
};

export const StatusSubSidePanel = ({
  status,
  sidePanelGroup,
  filteredRowsData,
}: {
  status: RegistrationStatus["status"];
  sidePanelGroup: SidePanelGroup;
  filteredRowsData: GridViewRegistration[];
}) => {
  const { t } = useTranslation();
  const gridApiRef = DynamicGrid.useInitialisedGridApiRef();
  const statusMap = getStatusMap(filteredRowsData);
  const registrations = statusMap.get(status) ?? [];

  return (
    <SidePanelGrid
      gridHeader={t("RegistrationGrid.SidePanels.StatusHeader", { status: t(`RegistrationStatus.${status}`) })}
      sidePanelGroup={sidePanelGroup}
      registrations={registrations}
      onShowInTableClick={() => {
        gridApiRef.current.upsertFilterItem({
          field: "status",
          operator: "is",
          value: t(`RegistrationStatus.${status}`),
        });
      }}
    />
  );
};

const getStatusMap = (data: GridViewRegistration[]) => {
  const statusMap = new Map<RegistrationStatus["status"], GridViewRegistration[]>();
  data
    .filter(registration => !registration.managedBy)
    .forEach(registration => {
      const status = registration.status.status;
      if (!statusMap.has(status)) statusMap.set(status, []);
      statusMap.get(status)?.push(registration);
    });
  return statusMap;
};
